
import * as React from 'react';
import { WithAction, IActionStateProps } from '../withAction/WithAction';
import { wait } from '../../utilities/async/wait';
import { TimeoutError } from '../../utilities/async/TimeoutError';
import { ISimpleComponentDecorator, ISimpleDecoratedProps } from '../../utilities/components/Decorators';

/**
 * @public
 */
export interface IAutoActionProps {
  onStart?: () => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

/**
 * @public
 */
export type IDecorateAsAutoActionProps<P extends {}> = ISimpleDecoratedProps<P, IAutoActionProps>;

/**
 * @public
 */
export function asAutoAction(options: {
  /**
   * A rate limit to apply befor an action can be executed again. Default is 0 ms.
   */
  rateLimit?: number;
  /**
   * A timeout to apply while waiting for the first execution of the action.
   * If the action does not execute before the timeout, the `onError` callback is invoked
   * with a `TimeoutError` and the action will never execute at all.
   */
  timeout?: number;
} = {}): ISimpleComponentDecorator<IAutoActionProps> {
  return <P extends {}>(
    component: React.ComponentType<P>
  ): React.ComponentType<P & IAutoActionProps> => {
    const Component = component;

    const {
      rateLimit = 0,
      timeout
    } = options;

    interface IAutoActionExecutorState {
      isExecuting?: boolean;
      isTimedOut?: boolean;
    }

    class AutoActionExecutor extends React.PureComponent<IAutoActionProps & IActionStateProps, IAutoActionExecutorState> {
      private _isDisposed?: boolean;
      private _timeoutId?: number;

      constructor(props: IAutoActionProps & IActionStateProps) {
        super(props);

        this._onTimeout = this._onTimeout.bind(this);
        this.state = {};
      }

      public render(): JSX.Element | null {
        return null;
      }

      public componentDidMount(): void {
        if (this._mayExecute(this.props, this.state)) {
          this._execute();
        } else if (typeof timeout === 'number') {
          this._timeoutId = setTimeout(this._onTimeout, timeout);
        }
      }

      public componentDidUpdate(previousProps: IActionStateProps, previousState: IAutoActionExecutorState): void {
        const previousMayExecute = this._mayExecute(previousProps, previousState);
        const mayExecute = this._mayExecute(this.props, this.state);

        if (!previousMayExecute && mayExecute) {
          if (typeof this._timeoutId === 'number') {
            clearTimeout(this._timeoutId);
            this._timeoutId = undefined;
          }

          this._execute();
        }
      }

      public componentWillUnmount(): void {
        this._isDisposed = true;
      }

      private _mayExecute(props: IActionStateProps, state: IAutoActionExecutorState): boolean {
        return !state.isTimedOut && !state.isExecuting && props.isAvailable && !props.isToggled && props.executingCount === 0;
      }

      private _onTimeout(): void {
        const {
          onError
        } = this.props;

        this.setState({
          // This prevents the real action from ever firing.
          isTimedOut: true
        });

        if (onError) {
          onError(new TimeoutError({
            timeout: timeout || 0
          }));
        }
      }

      private async _execute(): Promise<void> {
        const {
          execute,
          onStart,
          onComplete,
          onError
        } = this.props;

        try {
          this.setState({
            isExecuting: true
          });

          if (onStart) {
            onStart();
          }

          await execute({});

          if (onComplete) {
            onComplete();
          }
        } catch (e) {
          if (onError) {
            onError(e);
          }
        } finally {
          await wait(rateLimit);

          if (!this._isDisposed) {
            this.setState({
              isExecuting: undefined
            });
          }
        }
      }
    }

    class AutoAction extends React.PureComponent<P & IAutoActionProps> {
      public render(): JSX.Element {
        const {
          onStart,
          onComplete,
          onError,
          ...actionProps
        } = this.props as IAutoActionProps;

        return (
          <WithAction
            action={ (
              <Component { ...(actionProps as P) } />
            ) }
          >
            { (actionState: IActionStateProps) => {
              return (
                <AutoActionExecutor
                  { ...actionState }
                  onComplete={ onComplete }
                  onError={ onError }
                  onStart={ onStart }
                />
              );
            } }
          </WithAction>
        );
      }
    }

    return AutoAction;
  };
}
